// ============================================================
// naics.ts — what a NAICS code IS, and what it means.
//
// MIRROR OF api/src/naics.js — keep the two in lockstep. (api/ does not depend
// on @harvus/shared, so the rule genuinely exists twice.) The client validates
// a code before the API and the classifier ever see it.
//
// The full NAICS table is ~1,000 six-digit industries and a table of codes with
// WRONG titles is worse than no table: somebody reads the industry name off a
// deal header and quotes it. So this carries only the part that is small, fixed
// and verifiable — the 20 SECTORS — and derives the sector from any code's
// first two digits, which is correct at whatever depth the code sits.
//
// Three sectors span a RANGE of prefixes (31-33, 44-45, 48-49), which is why a
// sector is looked up through a prefix list and not by string equality: the
// code 484110 has to resolve, and it does not start with the label "48-49".
// ============================================================

export const MIN_NAICS_LEN = 2;
export const MAX_NAICS_LEN = 6;

export interface NaicsSector {
  code: string;       // as published — a range where the sector spans one
  prefixes: string[]; // what a real code's first two digits can actually be
  title: string;
}

export const NAICS_LEVELS: Record<number, string> = {
  2: "Sector",
  3: "Subsector",
  4: "Industry Group",
  5: "NAICS Industry",
  6: "National Industry",
};

export const NAICS_SECTORS: NaicsSector[] = [
  { code: "11", prefixes: ["11"], title: "Agriculture, Forestry, Fishing and Hunting" },
  { code: "21", prefixes: ["21"], title: "Mining, Quarrying, and Oil and Gas Extraction" },
  { code: "22", prefixes: ["22"], title: "Utilities" },
  { code: "23", prefixes: ["23"], title: "Construction" },
  { code: "31-33", prefixes: ["31", "32", "33"], title: "Manufacturing" },
  { code: "42", prefixes: ["42"], title: "Wholesale Trade" },
  { code: "44-45", prefixes: ["44", "45"], title: "Retail Trade" },
  { code: "48-49", prefixes: ["48", "49"], title: "Transportation and Warehousing" },
  { code: "51", prefixes: ["51"], title: "Information" },
  { code: "52", prefixes: ["52"], title: "Finance and Insurance" },
  { code: "53", prefixes: ["53"], title: "Real Estate and Rental and Leasing" },
  { code: "54", prefixes: ["54"], title: "Professional, Scientific, and Technical Services" },
  { code: "55", prefixes: ["55"], title: "Management of Companies and Enterprises" },
  { code: "56", prefixes: ["56"], title: "Administrative and Support and Waste Management and Remediation Services" },
  { code: "61", prefixes: ["61"], title: "Educational Services" },
  { code: "62", prefixes: ["62"], title: "Health Care and Social Assistance" },
  { code: "71", prefixes: ["71"], title: "Arts, Entertainment, and Recreation" },
  { code: "72", prefixes: ["72"], title: "Accommodation and Food Services" },
  { code: "81", prefixes: ["81"], title: "Other Services (except Public Administration)" },
  { code: "92", prefixes: ["92"], title: "Public Administration" },
];

const SECTOR_BY_PREFIX = new Map<string, NaicsSector>();
for (const s of NAICS_SECTORS) for (const p of s.prefixes) SECTOR_BY_PREFIX.set(p, s);

/** What was typed → what gets stored, or null. Non-digits are stripped (people
 *  write "54-1511" and "NAICS 541511"); anything that is not a code is null. */
export const normalizeNaicsCode = (value?: string | number | null): string | null => {
  const digits = String(value ?? "").replace(/\D+/g, "");
  if (digits.length < MIN_NAICS_LEN || digits.length > MAX_NAICS_LEN) return null;
  if (!SECTOR_BY_PREFIX.has(digits.slice(0, 2))) return null;
  return digits;
};

/** Why a value was refused, for a form to show. null when it is fine — and an
 *  EMPTY value is fine: the field is optional and "no code yet" is a real state. */
export const naicsCodeError = (value?: string | number | null): string | null => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const digits = raw.replace(/\D+/g, "");
  if (!digits) return "A NAICS code is 2–6 digits.";
  if (digits.length < MIN_NAICS_LEN) return "A NAICS code is at least 2 digits.";
  if (digits.length > MAX_NAICS_LEN) return "A NAICS code is at most 6 digits.";
  if (!SECTOR_BY_PREFIX.has(digits.slice(0, 2))) {
    return `"${digits.slice(0, 2)}" is not a NAICS sector.`;
  }
  return null;
};

export const sectorFor = (code?: string | null): NaicsSector | null => {
  const c = normalizeNaicsCode(code);
  return c ? SECTOR_BY_PREFIX.get(c.slice(0, 2)) || null : null;
};

export const naicsSectorTitle = (code?: string | null): string | null =>
  sectorFor(code)?.title ?? null;

export const naicsLevel = (code?: string | null): string | null => {
  const c = normalizeNaicsCode(code);
  return c ? NAICS_LEVELS[c.length] ?? null : null;
};

/** One line for a header or a table cell. The stored description wins (it is
 *  more specific than the sector by construction); the sector title is the
 *  fallback, so a bare code never renders as a naked number. */
export const naicsLabel = (code?: string | null, description?: string | null): string => {
  const c = normalizeNaicsCode(code);
  if (!c) return "";
  const desc = String(description ?? "").trim() || naicsSectorTitle(c);
  return desc ? `${c} · ${desc}` : c;
};
